import React, { MouseEventHandler } from 'react';
import { styles } from './CarouselLeadVideo.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ProductCarousel, ProductCarouselProps } from '../ProductCarousel/ProductCarousel';
import AspectRatio from '@jbhi-fi/lanyard-ui/components/AspectRatio';
import { useTrackingContext } from '../Tracking';
import { GoToLink, GoToLinkProps } from '../GoToLink/GoToLink';
import { ContentfulYouTube } from '@jbhi-fi/apis/src/contentful/types';
import { YouTubeTransformer } from '../Contentful/transformers/YouTubeTransformer';
import { ASPECT_RATIOS, PlayerState, PlayerStateChangeEvent, YouTubePlayer } from '../YouTubePlayer/YouTubePlayer';

export type CarouselLeadVideoProps = {
  video?: ContentfulYouTube;
  desktopCoverImage?: string;
  mobileCoverImage?: string;
  linkProps?: GoToLinkProps;
  ctaButton?: React.ReactNode;
  productCarouselProps: ProductCarouselProps;
};

export const CarouselLeadVideo = ({
  video,
  linkProps,
  ctaButton,
  productCarouselProps,
  desktopCoverImage,
  mobileCoverImage,
}: CarouselLeadVideoProps) => {
  const isDesktop = useBreakpoint('md', 'up');
  const { tracker } = useTrackingContext();
  if (!tracker.isEmpty) {
    tracker.group =
      tracker.group ??
      productCarouselProps?.trackingDescriptor?.ecommerceExtraProps.list ??
      productCarouselProps.heading;
  }

  const handleVideoPlayPause = (event: PlayerStateChangeEvent) => {
    if ([PlayerState.PAUSED, PlayerState.PLAYING].includes(event.state)) {
      tracker.trackClick({
        label: video?.fields?.name,
        extraData: {
          mediaType: 'video',
          mediaAction: event.state === PlayerState.PLAYING ? 'play' : 'pause',
          moduleName: 'Carousel lead video',
        },
      });
    }
  };

  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = e => {
    tracker.trackClick({ label: linkProps?.label, extraData: { moduleName: 'Carousel lead video link' } });
    linkProps?.onClick?.(e);
  };

  const { landscapeThumbnailUrl, portraitThumbnailUrl, ...youTubeProps } = video
    ? YouTubeTransformer.toYouTubePlayerProps(video)
    : {};

  const landscapeCoverImage = landscapeThumbnailUrl ?? mobileCoverImage;
  const portraitCoverImage = portraitThumbnailUrl ?? desktopCoverImage;

  return (
    <div className={styles.wrapper}>
      {linkProps ? <GoToLink {...linkProps} className={styles.link} onClick={handleLinkClick} /> : null}
      <div className={styles.container}>
        {video ? (
          <div className={styles.leadContainer}>
            <AspectRatio ratio={isDesktop ? ASPECT_RATIOS.portrait : ASPECT_RATIOS.landscape}>
              <YouTubePlayer
                {...youTubeProps}
                orientation={isDesktop ? 'Portrait' : 'Landscape'}
                onStateChange={handleVideoPlayPause}
                landscapeThumbnailUrl={landscapeCoverImage}
                portraitThumbnailUrl={portraitCoverImage}
              />
            </AspectRatio>
          </div>
        ) : null}
        <div className={styles.carouselContainer}>
          <ProductCarousel {...productCarouselProps} />
        </div>
      </div>
      {ctaButton ? <div className={styles.ctaButton}>{ctaButton}</div> : null}
    </div>
  );
};
